import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useCartStore } from '@/store/cartStore'
import Button from '@/components/common/Button'
import { formatCurrency } from '@/utils/helpers'
import toast from 'react-hot-toast'

const ACCESSORIES = [
  {
    id: 'halter',
    nameKey: 'holder',
    price: 9.99,
    image: '🖼️',
    descriptionKey: 'holderDesc',
  },
  {
    id: 'warnweste',
    nameKey: 'vest',
    price: 4.99,
    image: '🦺',
    descriptionKey: 'vestDesc',
  },
  {
    id: 'warndreieck',
    nameKey: 'triangle',
    price: 8.99,
    image: '⚠️',
    descriptionKey: 'triangleDesc',
  },
  {
    id: 'parkscheibe',
    nameKey: 'parking',
    price: 2.99,
    image: '🅿️',
    descriptionKey: 'parkingDesc',
  },
  {
    id: 'verbandskasten',
    nameKey: 'firstAid',
    price: 19.99,
    image: '🏥',
    descriptionKey: 'firstAidDesc',
  },
  {
    id: 'feuerloscher',
    nameKey: 'extinguisher',
    price: 29.99,
    image: '🧯',
    descriptionKey: 'extinguisherDesc',
  },
  {
    id: 'warnkegel',
    nameKey: 'cones',
    price: 12.99,
    image: '🔺',
    descriptionKey: 'conesDesc',
  },
]

const AccessoriesSection = () => {
  const { t } = useTranslation()
  const { addItem } = useCartStore()

  const handleAddToCart = (accessory: typeof ACCESSORIES[number]) => {
    addItem({
      productId: `acc-${accessory.id}`,
      productName: t(`accessories.${accessory.nameKey}`),
      price: accessory.price,
      quantity: 1,
    })

    toast.success(t('toast.productAdded'))
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gradient mb-3 sm:mb-4">{t('shop.accessories.title')}</h2>
        <p className="text-base sm:text-lg text-gray-400">
          {t('shop.accessories.description')}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {ACCESSORIES.map((accessory, index) => (
          <motion.div
            key={accessory.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -10, scale: 1.02 }}
            className="card-hover group"
          >
            <Link to={`/product/acc-${accessory.id}`} className="block">
              <div className="text-5xl sm:text-6xl mb-4 sm:mb-6 text-center group-hover:scale-110 transition-transform duration-300">
                {accessory.image}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-gray-100 group-hover:text-primary-400 transition-colors">
                {t(`accessories.${accessory.nameKey}`)}
              </h3>
            </Link>
            <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 min-h-[3rem]">
              {t(`accessories.${accessory.descriptionKey}`)}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-2xl sm:text-3xl font-bold text-primary-400">
                {formatCurrency(accessory.price)}
              </span>
              <Button onClick={() => handleAddToCart(accessory)} size="md">
                {t('shop.accessories.addToCart')}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

export default AccessoriesSection
